import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { PlusCircle, Search, Filter, ArrowUpDown, MoreVertical, ExternalLink, Settings, Trash2, AlertCircle, BarChart } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useMetaPixel } from "../../hooks/use-meta-pixel";
import { useGoogleAnalytics } from "../../hooks/use-google-analytics";
import { useGoogleAds } from "../../hooks/use-google-ads";
import websiteService from "../../services/websiteService";
import websitePageSpeedService from "../../services/WebsitePageSpeedService";
import AddWebsiteModal from "./components/AddWebsiteModal";
import EditWebsiteModal from "./components/EditWebsiteModal";
import DeleteConfirmationModal from "./components/DeleteConfirmationModal";
import PageSpeedAnalysisModal from "./components/PageSpeedAnalysisModal";
import Toast from "../../components/Toast";

// Calculate performance score based on Lighthouse methodology
// Weights from https://developer.chrome.com/docs/lighthouse/performance/performance-scoring/
const calculatePerformanceScore = (metrics) => {
  if (!metrics || !metrics.first_paint || !metrics.load_time) return null;
  
  // Define metric weights as per Lighthouse
  const weights = {
    FCP: 0.10,  // First Contentful Paint
    LCP: 0.25,  // Largest Contentful Paint (using first_paint as a proxy)
    TBT: 0.30,  // Total Blocking Time
    CLS: 0.15,  // Cumulative Layout Shift (assuming 0)
    SI: 0.10,   // Speed Index
    TTI: 0.10   // Time to Interactive (using load_time as a proxy)
  };
  
  // Calculate the scores for each metric using the Lighthouse scoring approach
  // These are simplified approximations based on the data we have
  const fcpScore = calculateFCPScore(metrics.first_paint);
  const lcpScore = calculateLCPScore(metrics.first_paint); // Using first_paint as proxy for LCP
  const tbtScore = calculateTBTScore(metrics.cpu_load_percent); // Using CPU load as a proxy for TBT
  const clsScore = 1.0; // Assuming CLS is great (1.0)
  const siScore = calculateSIScore(metrics.load_time);
  const ttiScore = calculateTTIScore(metrics.load_time);
  
  // Calculate weighted average
  const weightedScore = 
    weights.FCP * fcpScore +
    weights.LCP * lcpScore +
    weights.TBT * tbtScore +
    weights.CLS * clsScore +
    weights.SI * siScore +
    weights.TTI * ttiScore;
  
  // Return a score between 0-100
  return Math.round(weightedScore * 100);
};

// Scoring functions based on Lighthouse curves
// These are simplified versions of the actual Lighthouse scoring curves
const calculateFCPScore = (fcp) => {
  // FCP scoring: 0-1800ms (good), 1800-3000ms (needs improvement), >3000ms (poor)
  if (fcp <= 1800) return 1.0;
  if (fcp <= 3000) return 0.9 - (fcp - 1800) / 12000;
  return Math.max(0, 0.9 - (fcp - 3000) / 6000);
};

const calculateLCPScore = (lcp) => {
  // LCP scoring: 0-2500ms (good), 2500-4000ms (needs improvement), >4000ms (poor)
  if (lcp <= 2500) return 1.0;
  if (lcp <= 4000) return 0.9 - (lcp - 2500) / 15000;
  return Math.max(0, 0.9 - (lcp - 4000) / 10000);
};

const calculateTBTScore = (cpu) => {
  // Using CPU load as proxy for TBT: <30% (good), 30-50% (needs improvement), >50% (poor)
  if (cpu <= 30) return 1.0;
  if (cpu <= 50) return 0.9 - (cpu - 30) / 200;
  return Math.max(0, 0.9 - (cpu - 50) / 100);
};

const calculateSIScore = (loadTime) => {
  // Speed Index scoring approximation based on load time
  if (loadTime <= 3000) return 1.0;
  if (loadTime <= 5000) return 0.9 - (loadTime - 3000) / 20000;
  return Math.max(0, 0.9 - (loadTime - 5000) / 15000);
};

const calculateTTIScore = (loadTime) => {
  // TTI scoring approximation based on load time
  if (loadTime <= 3500) return 1.0;
  if (loadTime <= 5500) return 0.9 - (loadTime - 3500) / 20000;
  return Math.max(0, 0.9 - (loadTime - 5500) / 15000);
};

const Websites = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { trackWebsiteAdded } = useMetaPixel();
  const { trackWebsiteAdded: trackGAWebsiteAdded } = useGoogleAnalytics();
  const { trackWebsiteAdded: trackAdsWebsiteAdded } = useGoogleAds();
  const [searchQuery, setSearchQuery] = useState("");
  const [platformFilter, setPlatformFilter] = useState("all");
  const [sortField, setSortField] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");
  const [activeDropdown, setActiveDropdown] = useState(null);
  
  // Websites data state
  const [websites, setWebsites] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Modal states
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedWebsite, setSelectedWebsite] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // State for processing and toast notifications
  const [processingWebsite, setProcessingWebsite] = useState(false);
  const [processingStatus, setProcessingStatus] = useState('');
  const [toast, setToast] = useState({ visible: false, message: "", type: "" });
  
  // PageSpeed analysis state
  const [pageSpeedAnalysis, setPageSpeedAnalysis] = useState({
    isRunning: false,
    progress: 0,
    status: 'starting',
    message: 'Initializing...',
    websiteId: null
  });

  // Fetch websites from API - wrapping in useCallback to prevent recreation on each render
  const fetchWebsites = useCallback(async () => {
    if (!user) return;

    setIsLoading(true);
    setError(null);
    
    try {
      const response = await websiteService.getUserWebsites();
      console.log("Fetched websites:", response);
      
      // Get the websites array
      const websitesData = response.websites || [];
      
      // For each website, enhance with default values for missing fields
      const enhancedWebsites = websitesData.map(website => ({
        ...website,
        // Ensure these fields exist with default values if missing
        name: website.name || `Website ${website.id || 'Unknown'}`,
        root_url: website.root_url || website.url || website.website_url || website.domain || null,
        health_score: website.health_score || 0,
        mobile_health_score: website.mobile_health_score || 0,
        page_count: website.page_count || 0,
        last_scanned_at: website.last_scanned_at || null
      }));
      
      // For each website, fetch the latest PageSpeed data to display accurate speed and last checked
      const websitesWithPageSpeedData = await Promise.all(
        enhancedWebsites.map(async (website) => {
          // Skip PageSpeed data fetch if website is missing required fields
          if (!website || !website.id || !website.root_url) {
            console.log(`Skipping PageSpeed fetch for website with missing data:`, website);
            return website;
          }
          
          try {
            // Try to get the latest desktop PageSpeed data
            const desktopResponse = await websitePageSpeedService.getWebsitePageSpeedById(website.id, { 
              is_mobile: false,
              limit: 1
            });
            
            // Extract PageSpeed analysis data if available for desktop
            let desktopPageSpeedData = null;
            if (desktopResponse?.success && desktopResponse?.analyses?.length > 0) {
              desktopPageSpeedData = desktopResponse.analyses[0];
            } else if (desktopResponse?.success && desktopResponse?.pageSpeeds?.length > 0) {
              desktopPageSpeedData = desktopResponse.pageSpeeds[0];
            }
            
            // Try to get the latest mobile PageSpeed data
            const mobileResponse = await websitePageSpeedService.getWebsitePageSpeedById(website.id, { 
              is_mobile: true,
              limit: 1
            });
            
            // Extract PageSpeed analysis data if available for mobile
            let mobilePageSpeedData = null;
            if (mobileResponse?.success && mobileResponse?.analyses?.length > 0) {
              mobilePageSpeedData = mobileResponse.analyses[0];
            } else if (mobileResponse?.success && mobileResponse?.pageSpeeds?.length > 0) {
              mobilePageSpeedData = mobileResponse.pageSpeeds[0];
            }
            
            // Initialize enhanced website data
            let enhancedWebsiteData = { ...website };
            
            // If desktop PageSpeed data was found, enrich the website with this data
            if (desktopPageSpeedData) {
              console.log("Desktop PageSpeed data:", desktopPageSpeedData);
              
              // Initialize variables to store extracted data
              let desktopPerformanceScore = null;
              let desktopLoadTime = null;
              let desktopRawMetrics = null;
              let desktopRawData = null;
              
              // Try to parse the raw_metrics to get performance data
              if (desktopPageSpeedData.raw_metrics) {
                try {
                  desktopRawMetrics = typeof desktopPageSpeedData.raw_metrics === 'string' 
                    ? JSON.parse(desktopPageSpeedData.raw_metrics) 
                    : desktopPageSpeedData.raw_metrics;
                  
                  // Get load time from raw metrics
                  desktopLoadTime = desktopRawMetrics.load_time || desktopRawMetrics.page_load_time || null;
                  
                  // If raw_data is also available, try to extract Lighthouse performance score
                  if (desktopPageSpeedData.raw_data) {
                    try {
                      const parsedData = typeof desktopPageSpeedData.raw_data === 'string'
                        ? JSON.parse(desktopPageSpeedData.raw_data)
                        : desktopPageSpeedData.raw_data;
                        
                      desktopRawData = parsedData.raw_data 
                        ? (typeof parsedData.raw_data === 'string' ? JSON.parse(parsedData.raw_data) : parsedData.raw_data) 
                        : parsedData;
                      
                      // Use our custom calculation instead of the Lighthouse score
                      // even if Lighthouse performance score is available
                    } catch (parseError) {
                      console.log("Error parsing desktop raw_data:", parseError);
                    }
                  }
                  
                  // Calculate our own performance score using raw metrics
                  if (desktopRawMetrics && desktopRawMetrics.first_paint && desktopRawMetrics.load_time) {
                    desktopPerformanceScore = calculatePerformanceScore(desktopRawMetrics);
                  }
                  
                  // Update website data with desktop metrics
                  enhancedWebsiteData = {
                    ...enhancedWebsiteData,
                    // Use the PageSpeed performance score if available, otherwise use health_score
                    health_score: desktopPerformanceScore !== null ? desktopPerformanceScore : website.health_score,
                    // Update last_scanned_at with the PageSpeed analysis timestamp
                    last_scanned_at: desktopPageSpeedData.created_at || desktopPageSpeedData.analyzed_at || website.last_scanned_at,
                    // Store page load time for display in the speed column
                    page_load_time: desktopLoadTime,
                    // Store raw metrics for potential use in UI
                    raw_metrics: desktopRawMetrics,
                    // Store raw lighthouse data for potential use in UI
                    raw_data: desktopRawData
                  };
                } catch (parseError) {
                  console.log("Error parsing desktop raw_metrics:", parseError);
                }
              }
            }
            
            // If mobile PageSpeed data was found, add it to the enhanced website data
            if (mobilePageSpeedData) {
              console.log("Mobile PageSpeed data:", mobilePageSpeedData);
              
              // Initialize variables for mobile data
              let mobilePerformanceScore = null;
              let mobileLoadTime = null;
              let mobileRawMetrics = null;
              let mobileRawData = null;
              
              // Parse mobile raw_metrics
              if (mobilePageSpeedData.raw_metrics) {
                try {
                  mobileRawMetrics = typeof mobilePageSpeedData.raw_metrics === 'string' 
                    ? JSON.parse(mobilePageSpeedData.raw_metrics) 
                    : mobilePageSpeedData.raw_metrics;
                  
                  mobileLoadTime = mobileRawMetrics.load_time || mobileRawMetrics.page_load_time || null;
                  
                  // Parse mobile raw_data if available
                  if (mobilePageSpeedData.raw_data) {
                    try {
                      const parsedData = typeof mobilePageSpeedData.raw_data === 'string'
                        ? JSON.parse(mobilePageSpeedData.raw_data)
                        : mobilePageSpeedData.raw_data;
                        
                      mobileRawData = parsedData.raw_data 
                        ? (typeof parsedData.raw_data === 'string' ? JSON.parse(parsedData.raw_data) : parsedData.raw_data) 
                        : parsedData;
                      
                    } catch (parseError) {
                      console.log("Error parsing mobile raw_data:", parseError);
                    }
                  }
                  
                  // Calculate mobile performance score
                  if (mobileRawMetrics && mobileRawMetrics.first_paint && mobileRawMetrics.load_time) {
                    mobilePerformanceScore = calculatePerformanceScore(mobileRawMetrics);
                  }
                  
                  // Update website with mobile data
                  enhancedWebsiteData = {
                    ...enhancedWebsiteData,
                    mobile_health_score: mobilePerformanceScore !== null ? mobilePerformanceScore : (website.mobile_health_score || 0),
                    mobile_page_load_time: mobileLoadTime,
                    mobile_raw_metrics: mobileRawMetrics,
                    mobile_raw_data: mobileRawData,
                    // If desktop data is not available, use mobile data for last scan time
                    last_scanned_at: enhancedWebsiteData.last_scanned_at || mobilePageSpeedData.created_at || mobilePageSpeedData.analyzed_at || website.last_scanned_at
                  };
                } catch (parseError) {
                  console.log("Error parsing mobile raw_metrics:", parseError);
                }
              }
            }
            
            return enhancedWebsiteData;
          } catch (error) {
            console.log(`Error fetching PageSpeed data for website ${website.id}:`, error);
            return website;
          }
        })
      );
      
      setWebsites(websitesWithPageSpeedData);
    } catch (error) {
      console.error("Error fetching websites:", error);
      setError("Failed to load websites. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (activeDropdown && !event.target.closest('.dropdown-container')) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [activeDropdown]);

  // Fetch websites on component mount
  useEffect(() => {
    fetchWebsites();
  }, [fetchWebsites]);

  // Add listener for before unload when PageSpeed analysis is running
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (pageSpeedAnalysis.isRunning) {
        // Standard way to show confirmation message before leaving
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [pageSpeedAnalysis.isRunning]);

  // Handle website creation
  const handleCreateWebsite = async (websiteData) => {
    setIsSubmitting(true);
    setProcessingWebsite(true);
    setProcessingStatus('Submitting...');
    
    try {
      // Include user_id in the request
      const dataToSubmit = {
        ...websiteData,
        user_id: user.id
      };
      
      // Create the website
      setProcessingStatus('Creating website...');
      const response = await websiteService.createWebsite(dataToSubmit);
      
      // Show processing toast
      setToast({
        visible: true,
        message: "Website added. Processing and analyzing website...",
        type: "info"
      });
      
      try {
        // Extract website from the response
        const website = response.website || response;
        
        // Wait for website crawling to complete
        setProcessingStatus('Crawling website content...');
        const processedWebsiteResponse = await websiteService.waitForCrawlingComplete(website.id);
        
        // Extract the processed website from the response
        const processedWebsite = processedWebsiteResponse.website || processedWebsiteResponse;
        
        console.log("Crawling complete:", processedWebsite);
        
        // Update the website in our state with the processed data
        setWebsites(prev => [processedWebsite, ...prev]);
        
        // Track website addition
        trackWebsiteAdded();

        // Track website addition with Google Analytics
        trackGAWebsiteAdded();

        // Track website addition with Google Ads
        trackAdsWebsiteAdded({
          value: 15, // Assign value to website addition conversion
          currency: 'USD'
        });
        
        // Close add website modal
        setIsAddModalOpen(false);
        
        // Show PageSpeed analysis modal and start analysis
        await runPageSpeedAnalysis(processedWebsite);
        
      } catch (crawlingError) {
        console.error("Error during website crawling:", crawlingError);
        
        // If crawling timed out or failed, still add the website but with a warning
        const website = response.website || response;
        setWebsites(prev => [website, ...prev]);
        
        // Track website addition
        trackWebsiteAdded();

        // Track website addition with Google Analytics
        trackGAWebsiteAdded();

        // Track website addition with Google Ads
        trackAdsWebsiteAdded({
          value: 15, // Assign value to website addition conversion
          currency: 'USD'
        });
        
        // Close add website modal
        setIsAddModalOpen(false);
        
        // Try to run PageSpeed analysis anyway
        await runPageSpeedAnalysis(website);
      }
    } catch (error) {
      console.error("Error creating website:", error);
      setToast({
        visible: true,
        message: "Failed to create website. Please try again.",
        type: "error"
      });
    } finally {
      setIsSubmitting(false);
      setProcessingWebsite(false);
      setProcessingStatus('');
    }
  };
  
  // Run PageSpeed analysis
  const runPageSpeedAnalysis = async (website) => {
    if (!website || !website.root_url) {
      console.error("Cannot run PageSpeed analysis - invalid website data");
      return;
    }
    
    try {
      // Set initial state
      setProcessingStatus('Starting PageSpeed analysis...');
      setPageSpeedAnalysis({
        isRunning: true,
        progress: 0,
        status: 'starting',
        message: 'Initializing PageSpeed analysis...',
        websiteId: website.id
      });
      
      // Run both mobile and desktop analysis
      await websitePageSpeedService.analyzeWebsite(website, {
        mobile: true,
        desktop: true,
        onProgress: (progress) => {
          // Update processing status based on progress
          if (progress.status === 'analyzing-desktop') {
            setProcessingStatus('Analyzing desktop performance...');
          } else if (progress.status === 'analyzing-mobile') {
            setProcessingStatus('Analyzing mobile performance...');
          } else if (progress.status === 'complete') {
            setProcessingStatus('Analysis complete!');
          }
          
          setPageSpeedAnalysis({
            isRunning: progress.status !== 'complete' && progress.status !== 'error',
            progress: progress.progress,
            status: progress.status,
            message: progress.message,
            websiteId: website.id
          });
        }
      });
      
      // Analysis completed successfully - fetch the latest data
      try {
        // Get the fresh PageSpeed data for this website
        const desktopResponse = await websitePageSpeedService.getWebsitePageSpeedById(website.id, { 
          is_mobile: false,
          limit: 1
        });
        
        // Extract PageSpeed analysis data if available
        let pageSpeedData = null;
        if (desktopResponse?.success && desktopResponse?.analyses?.length > 0) {
          pageSpeedData = desktopResponse.analyses[0];
        } else if (desktopResponse?.success && desktopResponse?.pageSpeeds?.length > 0) {
          pageSpeedData = desktopResponse.pageSpeeds[0];
        }
        
        // If we got PageSpeed data, update this website in the list
        if (pageSpeedData) {
          let performanceScore = null;
          let loadTime = null;
          
          // Parse metrics data
          if (pageSpeedData.raw_metrics) {
            try {
              const rawMetrics = JSON.parse(pageSpeedData.raw_metrics);
              loadTime = rawMetrics.load_time || rawMetrics.page_load_time || null;
              
              if (pageSpeedData.raw_data) {
                try {
                  const parsedData = JSON.parse(pageSpeedData.raw_data);
                  const rawData = parsedData.raw_data ? JSON.parse(parsedData.raw_data) : parsedData;
                  
                  if (rawData?.categories?.performance?.score) {
                    performanceScore = Math.round(rawData.categories.performance.score * 100);
                  }
                } catch (parseError) {
                  console.log("Error parsing raw_data:", parseError);
                }
              }
            } catch (parseError) {
              console.log("Error parsing raw_metrics:", parseError);
            }
          }
          
          // Update this website in our state
          setWebsites(prev => 
            prev.map(site => {
              if (site.id === website.id) {
                return {
                  ...site,
                  health_score: performanceScore !== null ? performanceScore : site.health_score,
                  last_scanned_at: pageSpeedData.created_at || pageSpeedData.analyzed_at || new Date().toISOString(),
                  page_load_time: loadTime
                };
              }
              return site;
            })
          );
        }
      } catch (fetchError) {
        console.error("Error fetching updated PageSpeed data:", fetchError);
      }
      
      setToast({
        visible: true,
        message: "PageSpeed analysis complete! View the results in website details.",
        type: "success"
      });
      
    } catch (error) {
      console.error("Error during PageSpeed analysis:", error);
      
      // Update progress with error
      setPageSpeedAnalysis(prev => ({
        ...prev,
        isRunning: false,
        status: 'error',
        message: error.message || 'Error running PageSpeed analysis'
      }));
      
      setProcessingStatus('');
      
      setToast({
        visible: true,
        message: "PageSpeed analysis failed. You can retry it from website details page.",
        type: "error"
      });
    }
  };

  // Close toast notification
  const closeToast = () => {
    setToast({ ...toast, visible: false });
  };

  // Handle website update
  const handleUpdateWebsite = async (id, websiteData) => {
    setIsSubmitting(true);
    
    try {
      const response = await websiteService.updateWebsite(id, websiteData);
      
      // Extract website data from response
      const updatedWebsiteData = response.website || response;
      
      // Update the website in the state while preserving existing properties
      setWebsites(prev => 
        prev.map(website => {
          if (website.id === id) {
            // Create merged website object with updated data while preserving existing properties
            const mergedWebsite = {
              ...website, // Keep all existing properties
              ...updatedWebsiteData, // Override with updated properties
              // Explicitly keep important properties
              raw_metrics: website.raw_metrics || null,
              raw_data: website.raw_data || null,
              // Preserve these fields if they exist in original but not in response
              page_load_time: updatedWebsiteData.page_load_time || website.page_load_time || null,
              health_score: updatedWebsiteData.health_score || website.health_score || 0,
              last_scanned_at: updatedWebsiteData.last_scanned_at || website.last_scanned_at || null
            };
            
            return mergedWebsite;
          }
          return website;
        })
      );
      
      // Show success toast
      setToast({
        visible: true,
        message: "Website updated successfully!",
        type: "success"
      });
      
      // Close the modal
      setIsEditModalOpen(false);
      setSelectedWebsite(null);
    } catch (error) {
      console.error("Error updating website:", error);
      // Show error toast
      setToast({
        visible: true,
        message: "Failed to update website. Please try again.",
        type: "error"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle website deletion
  const handleDeleteWebsite = async () => {
    if (!selectedWebsite) return;
    
    setIsDeleting(true);
    
    try {
      await websiteService.deleteWebsite(selectedWebsite.id);
      
      // Remove the website from the state
      setWebsites(prev => 
        prev.filter(website => website.id !== selectedWebsite.id)
      );
      
      // Close the modal
      setIsDeleteModalOpen(false);
      setSelectedWebsite(null);
    } catch (error) {
      console.error("Error deleting website:", error);
      alert("Failed to delete website. Please try again.");
    } finally {
      setIsDeleting(false);
    }
  };

  // Open edit modal
  const openEditModal = (website) => {
    setSelectedWebsite(website);
    setIsEditModalOpen(true);
    setActiveDropdown(null);
  };

  // Open delete confirmation modal
  const openDeleteModal = (website) => {
    setSelectedWebsite(website);
    setIsDeleteModalOpen(true);
    setActiveDropdown(null);
  };

  // Navigate to website details page
  const navigateToWebsiteDetails = (websiteId) => {
    navigate(`/dashboard/websites/${websiteId}`);
  };

  // Toggle dropdown menu
  const toggleDropdown = (id, e) => {
    e.stopPropagation(); // Prevent event from bubbling to the row click handler
    if (activeDropdown === id) {
      setActiveDropdown(null);
    } else {
      setActiveDropdown(id);
    }
  };

  // Toggle sort direction
  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Apply filters and sorting
  const filteredWebsites = websites
    .filter((website) => {
      // Apply search filter
      if (searchQuery && !website.name.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false;
      }
      
      // Apply platform filter
      if (platformFilter !== "all" && website.platform !== platformFilter) {
        return false;
      }
      
      return true;
    })
    .sort((a, b) => {
      // Sort by selected field
      const aValue = a[sortField];
      const bValue = b[sortField];
      
      // Handle string and number comparisons
      if (typeof aValue === "string" && typeof bValue === "string") {
        if (sortDirection === "asc") {
          return aValue.localeCompare(bValue);
        } else {
          return bValue.localeCompare(aValue);
        }
      } else {
        if (sortDirection === "asc") {
          return aValue - bValue;
        } else {
          return bValue - aValue;
        }
      }
    });

  // Get unique platforms for filter options, filtering out null/undefined values
  const platforms = [...new Set(websites.filter(website => website.platform).map((website) => website.platform))];

  // Format last scanned date
  const formatLastChecked = (lastScannedAt) => {
    if (!lastScannedAt) return "Never";
    
    const lastScanned = new Date(lastScannedAt);
    const now = new Date();
    const diffMs = now - lastScanned;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffMins < 60) return `${diffMins} min${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
  };

  return (
    <div className="container p-4 mx-auto">
      {/* Toast notification */}
      {toast.visible && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={closeToast}
          duration={5000}
        />
      )}

      <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Websites</h1>
          <p className="text-gray-500">Manage and monitor your websites</p>
        </div>
        <div className="mt-4 md:mt-0">
          <button 
            onClick={() => setIsAddModalOpen(true)}
            className="inline-flex items-center gap-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            <PlusCircle className="h-4 w-4" />
            Add Website
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          {/* Search */}
          <div className="relative flex-1">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <Search className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="text"
              className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 p-2.5"
              placeholder="Search websites..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {/* Platform Filter */}
          <div className="flex-none md:w-48">
            <label htmlFor="platform-filter" className="sr-only">Platform</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <Filter className="h-4 w-4 text-gray-400" />
              </div>
              <select
                id="platform-filter"
                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 p-2.5"
                value={platformFilter}
                onChange={(e) => setPlatformFilter(e.target.value)}
              >
                <option value="all">All Platforms</option>
                {platforms.map((platform, index) => (
                  <option key={`platform-${platform}-${index}`} value={platform}>{platform}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Error state */}
      {error && (
        <div className="bg-red-100 border border-red-200 text-red-800 px-4 py-3 rounded-lg mb-6 flex items-center gap-2">
          <AlertCircle className="h-5 w-5" />
          <span>{error}</span>
        </div>
      )}

      {/* Loading state */}
      {(isLoading || processingWebsite) && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <div className="inline-flex items-center gap-2">
            <svg className="animate-spin h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span className="text-gray-600">
              {isLoading ? "Loading websites..." : "Processing website..."}
            </span>
          </div>
        </div>
      )}

      {/* Websites Table */}
      {!isLoading && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3">
                    <button 
                      className="flex items-center"
                      onClick={() => toggleSort("name")}
                    >
                      Website Name 
                      <ArrowUpDown className="ml-1 h-3 w-3" />
                    </button>
                  </th>
                  <th scope="col" className="px-6 py-3">
                    <button 
                      className="flex items-center"
                      onClick={() => toggleSort("platform")}
                    >
                      Platform
                      <ArrowUpDown className="ml-1 h-3 w-3" />
                    </button>
                  </th>
                  <th scope="col" className="px-6 py-3 text-center">
                    <button 
                      className="flex items-center justify-center mx-auto"
                      onClick={() => toggleSort("health_score")}
                    >
                      Performance
                      <ArrowUpDown className="ml-1 h-3 w-3" />
                    </button>
                  </th>
                  <th scope="col" className="px-6 py-3">
                    Pages
                  </th>
                  <th scope="col" className="px-6 py-3">
                    Last Checked
                  </th>
                  <th scope="col" className="px-6 py-3">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredWebsites.map((website) => {
                  return (
                    <tr 
                      key={website.id} 
                      className="bg-white border-b hover:bg-gray-50 cursor-pointer"
                      onClick={() => navigateToWebsiteDetails(website.id)}
                    >
                      <td className="px-6 py-4 font-medium text-gray-900">
                        <span className="text-blue-600 hover:underline">
                          {website.name}
                        </span>
                        <div className="text-xs text-gray-500 mt-1">
                          {website.root_url}
                        </div>
                      </td>
                      <td className="px-6 py-4">{website.platform}</td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          {/* Desktop Performance */}
                          <div className="flex items-center gap-2 mb-3">
                            <div className="text-xs text-gray-500 w-16">Desktop:</div>
                            <div className="flex items-center gap-2 flex-1">
                              <div className="h-2 w-20 rounded-full bg-gray-200">
                                <div
                                  className={`h-2 rounded-full ${
                                    website.health_score >= 90
                                      ? "bg-emerald-500" // Excellent (90-100)
                                      : website.health_score >= 80
                                      ? "bg-green-500" // Good (80-89)
                                      : website.health_score >= 70
                                      ? "bg-lime-500" // Fair (70-79)
                                      : website.health_score >= 50
                                      ? "bg-yellow-500" // Needs Improvement (50-69)
                                      : website.health_score >= 30
                                      ? "bg-orange-500" // Poor (30-49)
                                      : "bg-red-500" // Very Poor (0-29)
                                  }`}
                                  style={{ width: `${website.health_score || 0}%` }}
                                ></div>
                              </div>
                              <span className={`${
                                website.health_score >= 90
                                  ? "text-emerald-700"
                                  : website.health_score >= 80
                                  ? "text-green-700" 
                                  : website.health_score >= 70
                                  ? "text-lime-700"
                                  : website.health_score >= 50
                                  ? "text-yellow-700"
                                  : website.health_score >= 30
                                  ? "text-orange-700"
                                  : "text-red-700"
                              }`}>{website.health_score || 0}</span>
                            </div>
                          </div>

                          {/* Mobile Performance */}
                          <div className="flex items-center gap-2">
                            <div className="text-xs text-gray-500 w-16">Mobile:</div>
                            <div className="flex items-center gap-2 flex-1">
                              <div className="h-2 w-20 rounded-full bg-gray-200">
                                <div
                                  className={`h-2 rounded-full ${
                                    website.mobile_health_score >= 90
                                      ? "bg-emerald-500" // Excellent (90-100)
                                      : website.mobile_health_score >= 80
                                      ? "bg-green-500" // Good (80-89)
                                      : website.mobile_health_score >= 70
                                      ? "bg-lime-500" // Fair (70-79)
                                      : website.mobile_health_score >= 50
                                      ? "bg-yellow-500" // Needs Improvement (50-69)
                                      : website.mobile_health_score >= 30
                                      ? "bg-orange-500" // Poor (30-49)
                                      : "bg-red-500" // Very Poor (0-29)
                                  }`}
                                  style={{ width: `${website.mobile_health_score || 0}%` }}
                                ></div>
                              </div>
                              <span className={`${
                                website.mobile_health_score >= 90
                                  ? "text-emerald-700"
                                  : website.mobile_health_score >= 80
                                  ? "text-green-700" 
                                  : website.mobile_health_score >= 70
                                  ? "text-lime-700"
                                  : website.mobile_health_score >= 50
                                  ? "text-yellow-700"
                                  : website.mobile_health_score >= 30
                                  ? "text-orange-700"
                                  : "text-red-700"
                              }`}>{website.mobile_health_score || 0}</span>
                            </div>
                          </div>

                          {/* Load time info (if available) */}
                          {(website.page_load_time || website.mobile_page_load_time) && (
                            <div className="text-xs text-gray-500 mt-2">
                              {website.page_load_time && (
                                <div>Desktop: {(website.page_load_time / 1000).toFixed(1)}s</div>
                              )}
                              {website.mobile_page_load_time && (
                                <div>Mobile: {(website.mobile_page_load_time / 1000).toFixed(1)}s</div>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">{website.page_count || 0}</td>
                      <td className="px-6 py-4 text-gray-500">{formatLastChecked(website.last_scanned_at)}</td>
                      <td className="px-6 py-4 text-right dropdown-container" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={(e) => toggleDropdown(website.id, e)}
                          className="font-medium text-gray-500 hover:text-gray-900"
                        >
                          <MoreVertical className="h-5 w-5" />
                        </button>
                        
                        {/* Dropdown menu */}
                        {activeDropdown === website.id && (
                          <div className="fixed z-10 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200"
                               style={{
                                 top: 'auto',
                                 right: 'auto',
                                 transform: 'translate(-80%, 0)'
                               }}>
                            <div className="py-1">
                              <button 
                                key={`view-details-${website.id}`}
                                className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
                                onClick={() => navigateToWebsiteDetails(website.id)}
                              >
                                <BarChart className="h-4 w-4 mr-2" />
                                View Details & PageSpeed
                              </button>
                              <a 
                                key={`visit-${website.id}`}
                                href={website.root_url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                              >
                                <ExternalLink className="h-4 w-4 mr-2" />
                                Visit Website
                              </a>
                              <button 
                                key={`edit-${website.id}`}
                                className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
                                onClick={(e) => {
                                  e.stopPropagation(); // Prevent triggering the row click
                                  openEditModal(website);
                                }}
                              >
                                <Settings className="h-4 w-4 mr-2" />
                                Edit Settings
                              </button>
                              <button 
                                key={`delete-${website.id}`}
                                className="flex items-center px-4 py-2 text-sm text-red-600 hover:bg-gray-100 w-full text-left"
                                onClick={(e) => {
                                  e.stopPropagation(); // Prevent triggering the row click
                                  openDeleteModal(website);
                                }}
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete
                              </button>
                            </div>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
                
                {!isLoading && filteredWebsites.length === 0 && (
                  <tr key="no-websites-found" className="bg-white border-b">
                    <td colSpan="6" className="px-6 py-10 text-center text-gray-500">
                      No websites found. Try adjusting your filters or add a new website.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add Website Modal */}
      <AddWebsiteModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSave={handleCreateWebsite}
        isSubmitting={isSubmitting || processingWebsite}
        processingStatus={processingStatus}
      />

      {/* Edit Website Modal */}
      <EditWebsiteModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedWebsite(null);
        }}
        onSave={handleUpdateWebsite}
        website={selectedWebsite}
        isSubmitting={isSubmitting}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedWebsite(null);
        }}
        onConfirm={handleDeleteWebsite}
        websiteName={selectedWebsite?.name || ""}
        isDeleting={isDeleting}
      />
      
      {/* PageSpeed Analysis Modal */}
      <PageSpeedAnalysisModal
        isOpen={pageSpeedAnalysis.isRunning}
        status={pageSpeedAnalysis.status}
        message={pageSpeedAnalysis.message}
        progress={pageSpeedAnalysis.progress}
      />
    </div>
  );
};

export default Websites;